import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Car, User, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { useVehicleCard } from "@/hooks/useVehicleCard";
import { OwnerSearchDialog } from "./OwnerSearchDialog";
import { UserDetails, UserSummary } from "@/types/userDetails";
import { toast } from "sonner";

import { useNavigate } from "react-router-dom";
import { MakeSelectionDialog } from "@/admin/car-card/dialogs/MakeSelectionDialog";
import { ModelSelectionDialog } from "@/admin/car-card/dialogs/ModelSelectionDialog";
import { EngineSelectionDialog } from "@/admin/car-card/dialogs/EngineSelectionDialog";
import { getLanguage } from "@/utils/languageUtils";
import { vehicleCardUILabels } from "@/i18n/labels/vehicleCardLabels";
import {
  bodyTypeLabels,
  colorLabels,
  conditionGradeLabels,
  transmissionTypeLabels,
  driveTypeLabels,
  vehicleTypeLabels,
  CREATE_MANUAL_FUEL_KEYS,
  CREATE_MANUAL_BODY_KEYS,
  CREATE_MANUAL_DRIVE_KEYS,
  CREATE_MANUAL_COLOR_KEYS,
} from "@/i18n/labels/vehicleLabels";
import { TransmissionType, CONDITION_GRADE_OPTIONS } from "@/types/enums";
import { commonButtonLabels } from "@/i18n/labels/commonLabels";
import svVehicleCard from "@/i18n/locales/sv/vehicleCard.json";
import {
  buildCreateVehicleCardPayload,
  isValidModelYear,
  isValidMileage,
  isValidOptionalCount,
  numericFieldsValid,
  MIN_MODEL_YEAR,
  maxModelYear,
} from "./createVehicleCardPayload";
import enVehicleCard from "@/i18n/locales/en/vehicleCard.json";

interface CreateVehicleCardManuallyDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateVehicleCardManuallyDialog = ({
  open,
  onOpenChange,
}: CreateVehicleCardManuallyDialogProps) => {

  const navigate = useNavigate();
  const { language } = useLanguage();
  const lang = language || getLanguage();
  const ui = vehicleCardUILabels(lang);
  const { createVehicleCardManuallyAsync, isCreatingVehicleCardManually } =
    useVehicleCard();

  const [selectedOwner, setSelectedOwner] = useState<UserSummary | null>(null);
  const [showOwnerSearch, setShowOwnerSearch] = useState(false);
  const [vehicleDetails, setVehicleDetails] = useState({
    regNumber: "",
    vehicleType: "" as "car" | "mc" | "",
    make: "",
    model: "",
    engine: "",
    year: "",
    mileage: "",
    fuelType: "",
    drivetype: "",
    color: "",
    type: "",
    condition: "",
    transmission: "",
    horsepower: "",
  });

  // Dialog states for make/model/engine selection
  const [makeDialogOpen, setMakeDialogOpen] = useState(false);
  const [modelDialogOpen, setModelDialogOpen] = useState(false);
  const [engineDialogOpen, setEngineDialogOpen] = useState(false);

  // Error state
  const [errorMessage, setErrorMessage] = useState<string>("");

  const vehicleCardLocale = lang === "en" ? enVehicleCard : svVehicleCard;
  const fuelLabels = (vehicleCardLocale as any).fuelType || {};
  const fuelOpts = CREATE_MANUAL_FUEL_KEYS;
  const driveLabels = driveTypeLabels(lang);
  const bodyLabels = bodyTypeLabels(lang);
  const colorOptsLabels = colorLabels(lang);
  const conditionLabels = conditionGradeLabels(lang);
  // Gearbox. Same shape as the condition select: enum members as the option VALUE and
  // the shared label helper for display, so what is sent is already enum-compatible.
  // Note this is NOT `drivetype` - that answer (fwd/rwd/4wd/awd) is a different fact and
  // is no longer written into the transmission column.
  const yearRangeMessage = ui.yearFormat
    .replace("{min}", String(MIN_MODEL_YEAR))
    .replace("{max}", String(maxModelYear()));
  const transmissionLabels = transmissionTypeLabels(lang);
  const TRANSMISSION_KEYS = [
    TransmissionType.AUTOMATIC,
    TransmissionType.MANUAL,
    TransmissionType.SEMI_AUTOMATIC,
    TransmissionType.CVT,
    TransmissionType.OTHER,
  ];
  const CONDITION_KEYS = CONDITION_GRADE_OPTIONS;

  const handleInputChange = (field: string, value: string) => {
    setVehicleDetails((prev) => {
      const updated = {
        ...prev,
        [field]: value,
      };

      // If vehicleType changes, reset make, model, and engine if they're not compatible
      if (field === "vehicleType" && value !== prev.vehicleType) {
        // Reset make, model, and engine when vehicle type changes
        updated.make = "";
        updated.model = "";
        updated.engine = "";
      }

      return updated;
    });
    // Clear error message when user makes changes
    if (errorMessage) {
      setErrorMessage("");
    }
  };

  const handleMakeSelect = (newMake: string) => {
    const makeChanged = newMake !== vehicleDetails.make;
    handleInputChange("make", newMake);
    // Reset model and engine when make changes
    if (makeChanged) {
      handleInputChange("model", "");
      handleInputChange("engine", "");
    }
  };

  const handleModelSelect = (newModel: string) => {
    const modelChanged = newModel !== vehicleDetails.model;
    handleInputChange("model", newModel);
    // Reset engine when model changes
    if (modelChanged) {
      handleInputChange("engine", "");
    }
  };

  const handleEngineSelect = (newEngine: string) => {
    handleInputChange("engine", newEngine);
  };

  const handleSelectOwner = (owner: UserSummary) => {
    setSelectedOwner(owner);
    setShowOwnerSearch(false);
  };

  // Validate Swedish registration number format
  const validateRegNumber = (regNumber: string): boolean => {
    // Swedish registration number format: 3 letters + 3 digits (ABC123) or 3 letters + 2 digits + 1 letter (ABC12A)
    const swedishRegPattern = /^[A-Z]{3}[0-9]{2}[A-Z0-9]$/;
    return swedishRegPattern.test(regNumber);
  };

  // Check if form is valid
  const isFormValid = () => {
    const requiredFields = [
      "regNumber",
      "vehicleType",
      "make",
      "model",
      "year",
      "mileage",
      "fuelType",
      "color",
      ...(vehicleDetails.vehicleType !== "mc" ? ["drivetype", "type"] : []),
    ];

    const allFieldsFilled = requiredFields.every(
      (field) =>
        vehicleDetails[field as keyof typeof vehicleDetails].trim() !== "",
    );

    const regNumberValid = validateRegNumber(vehicleDetails.regNumber);
    const ownerSelected = !!selectedOwner;

    return (
      allFieldsFilled &&
      regNumberValid &&
      ownerSelected &&
      numericFieldsValid(vehicleDetails)
    );
  };

  const handleSubmit = async () => {
    // Validate required fields
    const requiredFields = [
      "regNumber",
      "vehicleType",
      "make",
      "model",
      "year",
      "mileage",
      "fuelType",
      "color",
      ...(vehicleDetails.vehicleType !== "mc" ? ["drivetype", "type"] : []),
    ];
    const missingFields = requiredFields.filter(
      (field) => !vehicleDetails[field as keyof typeof vehicleDetails],
    );

    if (missingFields.length > 0) {
      toast.error(ui.missingFields, { description: ui.missingFieldsDesc });
      return;
    }

    if (!selectedOwner) {
      toast.error(ui.missingOwner, { description: ui.missingOwnerDesc });
      return;
    }

    // Validate registration number format
    if (!validateRegNumber(vehicleDetails.regNumber)) {
      toast.error(ui.invalidRegNumber, { description: ui.invalidRegNumberDesc });
      return;
    }

    // Numeric fields: a NaN used to serialise to null and create a card with no year.
    if (!isValidModelYear(vehicleDetails.year)) {
      toast.error(ui.invalidYear, { description: yearRangeMessage });
      return;
    }
    if (!isValidMileage(vehicleDetails.mileage)) {
      toast.error(ui.invalidMileage, { description: ui.mileageFormat });
      return;
    }
    if (!isValidOptionalCount(vehicleDetails.horsepower)) {
      toast.error(ui.invalidHorsepower, { description: ui.horsepowerFormat });
      return;
    }

    // Clear previous error
    setErrorMessage("");

    // Create vehicle card
    try {
      const result = await createVehicleCardManuallyAsync(
        buildCreateVehicleCardPayload(selectedOwner._id, vehicleDetails),
      );

      toast.success(ui.vehicleCardCreated, {
        description: (
          ui.vehicleCardCreatedSuccess ||
          "Fordonskort har skapats framgångsrikt! ID: {id}"
        ).replace("{id}", result.data.vehicleCardId || result.data.id),
      });

      // Reset form
      setSelectedOwner(null);
      setVehicleDetails({
        regNumber: "",
        vehicleType: "" as "car" | "mc" | "",
        make: "",
        model: "",
        engine: "",
        year: "",
        mileage: "",
        fuelType: "",
        drivetype: "",
        color: "",
        type: "",
        condition: "",
        transmission: "",
        horsepower: "",
      });
      setErrorMessage("");

      onOpenChange(false);

      // Navigate to the new vehicle card
      navigate(`/admin/leadbox/vehicle-cards/${result.data.id}`);
    } catch (error: unknown) {
      // Handle 409 conflict (duplicate registration number)
      if (
        error &&
        typeof error === "object" &&
        "status" in error &&
        error.status === 409
      ) {
        setErrorMessage(ui.duplicateRegNumber);
      } else {
        // Handle other errors
        let errorMsg = ui.createError;

        if (error && typeof error === "object" && "message" in error) {
          errorMsg =
            typeof error.message === "string" ? error.message : errorMsg;
        } else if (error instanceof Error) {
          errorMsg = error.message;
        }

        setErrorMessage(errorMsg);
      }
    }
  };

  const handleClose = () => {
    // Reset form when closing
    setSelectedOwner(null);
    setVehicleDetails({
      regNumber: "",
      vehicleType: "" as "car" | "mc" | "",
      make: "",
      model: "",
      engine: "",
      year: "",
      mileage: "",
      fuelType: "",
      drivetype: "",
      color: "",
      type: "",
      condition: "",
      transmission: "",
      horsepower: "",
    });
    setErrorMessage("");
    onOpenChange(false);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Car className="h-5 w-5" />
              {commonButtonLabels(lang).createVehicleCardManually}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6">
            {/* Owner Selection */}
            <div className="space-y-2">
              <Label className="text-base font-medium">{ui.owner} *</Label>
              {selectedOwner ? (
                <div className="flex items-center justify-between p-3 border rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <User className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <div className="font-medium">
                        {`${selectedOwner.firstName} ${selectedOwner.lastName}`}
                      </div>
                      <div className="text-sm text-muted-foreground">
                        {selectedOwner.email}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSelectedOwner(null)}
                  >
                    {ui.change}
                  </Button>
                </div>
              ) : (
                <Button
                  variant="outline"
                  onClick={() => setShowOwnerSearch(true)}
                  className="w-full justify-start"
                >
                  <User className="h-4 w-4 mr-2" />
                  {ui.selectOwner}
                </Button>
              )}
            </div>

            {/* Vehicle Details */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">{ui.vehicleDetails}</h3>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="regNumber">
                    {ui.registrationNumberFull} *
                  </Label>
                  <div className="relative">
                    <Input
                      id="regNumber"
                      value={vehicleDetails.regNumber}
                      onChange={(e) =>
                        handleInputChange(
                          "regNumber",
                          e.target.value.toUpperCase(),
                        )
                      }
                      placeholder="ABC123"
                      className={
                        vehicleDetails.regNumber &&
                        !validateRegNumber(vehicleDetails.regNumber)
                          ? "border-red-500 focus:border-red-500"
                          : vehicleDetails.regNumber &&
                              validateRegNumber(vehicleDetails.regNumber)
                            ? "border-green-500 focus:border-green-500"
                            : ""
                      }
                    />
                    {vehicleDetails.regNumber && (
                      <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                        {validateRegNumber(vehicleDetails.regNumber) ? (
                          <div className="text-green-500 text-sm">✓</div>
                        ) : (
                          <div className="text-red-500 text-sm">✗</div>
                        )}
                      </div>
                    )}
                  </div>
                  {vehicleDetails.regNumber &&
                    !validateRegNumber(vehicleDetails.regNumber) && (
                      <p className="text-sm text-red-500">
                        {ui.regNumberFormat}
                      </p>
                    )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="vehicleType">{ui.vehicleTypeLabel} *</Label>
                  <Select
                    value={vehicleDetails.vehicleType}
                    onValueChange={(value) =>
                      handleInputChange("vehicleType", value as "car" | "mc")
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={ui.selectVehicleType} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="car">
                        {vehicleTypeLabels(lang).car}
                      </SelectItem>
                      <SelectItem value="mc">
                        {vehicleTypeLabels(lang).mc}
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="make">{ui.make} *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setMakeDialogOpen(true)}
                    className="w-full justify-start"
                  >
                    {vehicleDetails.make || ui.selectMake}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="model">{ui.model} *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setModelDialogOpen(true)}
                    disabled={!vehicleDetails.make}
                    className="w-full justify-start"
                  >
                    {vehicleDetails.model || ui.selectModel}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="engine">{ui.engine} *</Label>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setEngineDialogOpen(true)}
                    disabled={!vehicleDetails.make}
                    className="w-full justify-start"
                  >
                    {vehicleDetails.engine || ui.selectEngine}
                  </Button>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="year">{ui.year} *</Label>
                  <Input
                    id="year"
                    type="number"
                    value={vehicleDetails.year}
                    onChange={(e) => handleInputChange("year", e.target.value)}
                    placeholder="2020"
                    min={MIN_MODEL_YEAR}
                    max={maxModelYear()}
                    className={
                      vehicleDetails.year && !isValidModelYear(vehicleDetails.year)
                        ? "border-red-500 focus:border-red-500"
                        : ""
                    }
                  />
                  {vehicleDetails.year && !isValidModelYear(vehicleDetails.year) && (
                    <p className="text-sm text-red-500">{yearRangeMessage}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="mileage">{ui.mileageKm} *</Label>
                  <Input
                    id="mileage"
                    type="number"
                    value={vehicleDetails.mileage}
                    onChange={(e) =>
                      handleInputChange("mileage", e.target.value)
                    }
                    placeholder="50000"
                    min="0"
                    className={
                      vehicleDetails.mileage && !isValidMileage(vehicleDetails.mileage)
                        ? "border-red-500 focus:border-red-500"
                        : ""
                    }
                  />
                  {vehicleDetails.mileage && !isValidMileage(vehicleDetails.mileage) && (
                    <p className="text-sm text-red-500">{ui.mileageFormat}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="fuelType">{ui.fuelType} *</Label>
                  <Select
                    value={vehicleDetails.fuelType}
                    onValueChange={(value) =>
                      handleInputChange("fuelType", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={ui.selectFuelType} />
                    </SelectTrigger>
                    <SelectContent>
                      {fuelOpts.map((key) => (
                        <SelectItem key={key} value={key}>
                          {fuelLabels[key] ?? key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {vehicleDetails.vehicleType !== "mc" && (
                  <div className="space-y-2">
                    <Label htmlFor="drivetype">{ui.driveTypeLabel} *</Label>
                    <Select
                      value={vehicleDetails.drivetype}
                      onValueChange={(value) =>
                        handleInputChange("drivetype", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={ui.selectDriveType} />
                      </SelectTrigger>
                      <SelectContent>
                        {CREATE_MANUAL_DRIVE_KEYS.map((key) => (
                          <SelectItem key={key} value={key}>
                            {driveLabels[key] ?? key}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="color">{ui.color} *</Label>
                  <Select
                    value={vehicleDetails.color}
                    onValueChange={(value) => handleInputChange("color", value)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={ui.selectColor} />
                    </SelectTrigger>
                    <SelectContent>
                      {CREATE_MANUAL_COLOR_KEYS.map((key) => (
                        <SelectItem key={key} value={key}>
                          {colorOptsLabels[key] ?? key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {vehicleDetails.vehicleType !== "mc" && (
                  <div className="space-y-2">
                    <Label htmlFor="type">{ui.bodyType} *</Label>
                    <Select
                      value={vehicleDetails.type}
                      onValueChange={(value) =>
                        handleInputChange("type", value)
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder={ui.selectBodyType} />
                      </SelectTrigger>
                      <SelectContent>
                        {CREATE_MANUAL_BODY_KEYS.map((key) => (
                          <SelectItem key={key} value={key}>
                            {bodyLabels[key] ?? key}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="condition">{ui.condition}</Label>
                  <Select
                    value={vehicleDetails.condition}
                    onValueChange={(value) =>
                      handleInputChange("condition", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={ui.selectCondition} />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_KEYS.map((key) => (
                        <SelectItem key={key} value={key}>
                          {conditionLabels[key] ?? key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="transmission">{ui.transmission}</Label>
                  <Select
                    value={vehicleDetails.transmission}
                    onValueChange={(value) =>
                      handleInputChange("transmission", value)
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={ui.selectTransmission} />
                    </SelectTrigger>
                    <SelectContent>
                      {TRANSMISSION_KEYS.map((key) => (
                        <SelectItem key={key} value={key}>
                          {transmissionLabels[key] ?? key}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="horsepower">{ui.horsepower}</Label>
                  <Input
                    id="horsepower"
                    type="text"
                    value={vehicleDetails.horsepower}
                    onChange={(e) =>
                      handleInputChange("horsepower", e.target.value)
                    }
                    placeholder={ui.horsepowerPlaceholder}
                    className={
                      !isValidOptionalCount(vehicleDetails.horsepower)
                        ? "border-red-500 focus:border-red-500"
                        : ""
                    }
                  />
                  {!isValidOptionalCount(vehicleDetails.horsepower) && (
                    <p className="text-sm text-red-500">{ui.horsepowerFormat}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Error message display */}
            {errorMessage && (
              <div className="p-3 border border-red-500 rounded-lg bg-red-50 dark:bg-red-900/20">
                <p className="text-sm text-red-600 dark:text-red-400">
                  {errorMessage}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={handleClose}>
                {commonButtonLabels(lang).cancel}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={isCreatingVehicleCardManually || !isFormValid()}
              >
                {isCreatingVehicleCardManually ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {commonButtonLabels(lang).creating}
                  </>
                ) : (
                  commonButtonLabels(lang).createVehicleCard
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Owner Search Dialog */}
      <OwnerSearchDialog
        open={showOwnerSearch}
        onOpenChange={setShowOwnerSearch}
        onSelectOwner={handleSelectOwner}
      />

      {/* Make Selection Dialog */}
      <MakeSelectionDialog
        open={makeDialogOpen}
        onOpenChange={setMakeDialogOpen}
        selectedMake={vehicleDetails.make}
        onSelect={handleMakeSelect}
        vehicleType={vehicleDetails.vehicleType || "car"}
      />

      {/* Model Selection Dialog */}
      <ModelSelectionDialog
        open={modelDialogOpen}
        onOpenChange={setModelDialogOpen}
        selectedMake={vehicleDetails.make}
        selectedModel={vehicleDetails.model}
        onSelect={handleModelSelect}
        vehicleType={vehicleDetails.vehicleType || "car"}
      />

      {/* Engine Selection Dialog */}
      <EngineSelectionDialog
        open={engineDialogOpen}
        onOpenChange={setEngineDialogOpen}
        selectedMake={vehicleDetails.make}
        selectedEngine={vehicleDetails.engine}
        onSelect={handleEngineSelect}
        vehicleType={vehicleDetails.vehicleType || "car"}
      />
    </>
  );
};
